import { ProcessAlias } from 'api/endpoints/processFields';
import { ASWithStaticFieldsQuery } from 'api/endpoints/securityGroups';
import {
  atf_map,
  fta_map,
  NUMBER_FUNCTIONS,
  STRING_FUNCTIONS,
} from 'components/organisms/form/advancedSearchConfigure/config';
import { FormFields as AdvancedFields } from 'components/organisms/form/advancedSearchConfigure/types';
import { DataModelFieldType } from 'constants/global';
import { Filter, FilterItem, FilterOperator } from 'container/search';
import { Result } from 'hooks/useGetAdvancedSearchParams';

import { string } from '.';

const STATIC_FIELD_NAMES = ['created_at', 'updated_at', 'name'];

const normalizeField = (field: Result): SearchFilter => ({
  ...(typeof field.value !== 'undefined' && {
    value:
      field.type === DataModelFieldType.Number
        ? Number(field.value)
        : decodeURIComponent(field.value as string),
  }),
  ...(field.type === DataModelFieldType.Number
    ? NUMBER_FUNCTIONS[field.operation].config
    : STRING_FUNCTIONS[field.operation].config),
});

export const normalizeFieldValue = (type: string, value?: string) => {
  switch (type) {
    case DataModelFieldType.Date:
      // TODO: CREATE UTIL WITH NAMED REGULAR EXPRESSIONS
      return value?.replace(/\//g, '');
    default:
      return value;
  }
};

export const normalizeAdvancedValues = <T extends AdvancedFields>(
  entity: T
) => {
  const { fields, ...rest } = entity;
  const filteredFields = Object.fromEntries(
    Object.entries(fields || {}).filter(([, value]) => !!value)
  );

  return {
    ...rest,
    fields: filteredFields,
  };
};

export const normalizeAdvancedParams = (
  operator: FilterOperator,
  params: Result[]
) => {
  const dynamicFields = params.filter(
    (e) => !STATIC_FIELD_NAMES.includes(e.id)
  );

  const staticFields = params.filter((e) => STATIC_FIELD_NAMES.includes(e.id));

  return {
    operator,
    variables: dynamicFields.map((e): SearchDynamicVariable => ({
      aliasId: e.id,
      ...normalizeField(e),
    })),
    default_fields: staticFields.map((e): SearchStaticVariable => ({
      fieldName: e.id,
      ...normalizeField(e),
    })),
  };
};

type SearchParam = SearchFilter & {
  aliasId?: string;
  fieldName?: string;
};

export const normalizeSearchVariable =
  (idKey: 'aliasId' | 'fieldName') =>
  (e: SearchParam): Result => ({
    id: e[idKey] as string,
    operation: `${string.camelize(e.operation)}${e.not ? 'Not' : ''}`,
    type: typeof e.value === 'number' ? 'number' : 'string',
    ...(typeof e.value !== 'undefined' && { value: e.value }),
  });

export const excludeObjectProperty = <T extends {}>(
  object: T,
  ...exclude: string[]
) =>
  Object.fromEntries(
    Object.entries(object).filter(([key]) =>
      Array.isArray(exclude) ? !exclude.includes(key) : exclude !== key
    )
  );

const toFilterItem =
  <T extends string>(key: T, aliasIdMap: Record<string, ProcessAlias>) =>
  (filter: NamedSearchFilter<T>): FilterItem | null => {
    const { operation, not, value } = filter;

    const converted_operation = atf_map[operation];
    if (!converted_operation) return null; // there are some unsupported filter operations

    const field =
      filter[key] in aliasIdMap
        ? aliasIdMap[filter[key]].shortName
        : filter[key];

    return {
      operation: converted_operation,
      not,
      value,
      field,
    };
  };

export function toFilter(
  query: ASWithStaticFieldsQuery,
  aliases?: ProcessAlias[]
): Filter {
  const aliasIdMap = Object.fromEntries(
    aliases?.map((alias) => [alias.id, alias]) ?? []
  );

  return {
    operator: query.operator,
    items: [
      ...(query.variables
        ?.map(toFilterItem('aliasId', aliasIdMap))
        .filter((item) => !!item) ?? []),
      ...(query.defaultFields
        ?.map(toFilterItem('fieldName', aliasIdMap))
        .filter((item) => !!item) ?? []),
    ],
  };
}

const fromFilterItem =
  (aliasShortnameMap: Record<string, ProcessAlias>) =>
  (item: FilterItem): NamedSearchFilter<'aliasId' | 'fieldName'> | null => {
    const { field, operation, value, not } = item;

    const converted_operation = fta_map[operation];
    if (!converted_operation) return null; // there are some unsupported filter operations

    const key = STATIC_FIELD_NAMES.includes(field) ? 'fieldName' : 'aliasId';

    const converted_field =
      field in aliasShortnameMap ? aliasShortnameMap[field].id : field;

    return {
      [key]: converted_field,
      operation: converted_operation,
      not,
      value,
    } as NamedSearchFilter<typeof key>;
  };

export function fromFilter(
  filter: Filter,
  aliases?: ProcessAlias[]
): ASWithStaticFieldsQuery {
  const aliasShortNameMap = Object.fromEntries(
    aliases?.map((alias) => [alias.shortName, alias]) ?? []
  );

  const objs = Object.groupBy(
    filter.items
      .map(fromFilterItem(aliasShortNameMap))
      .filter((item) => item !== null),
    (item) => ('fieldName' in item ? 'defaultFields' : 'variables')
  );

  return {
    operator: filter.operator,
    defaultFields: objs.defaultFields,
    variables: objs.variables,
  };
}
